import { useState } from "react";
import styles from "./FinancialAccounts.module.css";

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { Add, Edit, Delete, Search, FilterAlt, ArrowBack, FilterAltOffSharp } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteAccount,
  getFinanceAccount,
} from "../../../../services/financeAccount";
import type { IResult } from "../../../../types/financeAccount";
import { formatCurrencyInput } from "../../../../utils/useMask";
import Filter from "./Filter";
import Modal from "./Modal";
import Button from "../../../../components/Button/Button";
import TableContainer from "../../../../components/TableContainer/TableContainer";
import InputSearch from "../../../../components/InputSearch/InputSearch";

const FinancialAccounts = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const [isHidden, setIsHidden] = useState<{
    add?: boolean;
    edit?: boolean;
    uid?: string;
  }>();

  const [dataFilter, setDataFilter] = useState<{
    tipo: string;
    status: string;
  }>({
    tipo: "",
    status: "",
  });

  const { mutate } = useMutation({
    mutationFn: async (data: string) => {
      const res = await deleteAccount(data);
      return res;
    },
    onSuccess: (response) => {
      toast.success(response.message || "Conta deletada com success");
      queryClient.invalidateQueries({ queryKey: ["getFinanceAccount"] });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery<IResult>({
    queryKey: ["getFinanceAccount", dataFilter, search],
    queryFn: ({ pageParam }) =>
      getFinanceAccount(dataFilter, pageParam as number, search),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const handleDelete = (uid: string) => {
    setConfirmDelete(uid);
  };

  const handleResetFilter = () => {
    setDataFilter({ tipo: "", status: "" });
  };

  return (
    <div className={styles.containerAccount}>
      <div className={styles.financeAccount}>
        <div className={styles.header}>
          <h1>Conta Financeira</h1>

          <InputSearch
              searchValue={search}
              setSearchValue={setSearch}
              placeholder="Pesquisar"
          />

          <div className={styles.headerButtons}>
            <p>
              Qtd:<span>{data ? data.pages.map((p) => p.total) : 0}</span>
            </p>
            <Button
              variant="contain"
              title="Criar nova movimentação"
              onClick={() => setIsHidden({ add: true })}
            >
              <Add />
            </Button>
            <Button
              variant="contain"
              title="Filtrar movimentação"
              onClick={() => setIsVisible(true)}
            >
              <FilterAlt />
            </Button>
            <Button
              variant="contain"
              onClick={handleResetFilter}
              title="Limpar filtros"
            >
              <FilterAltOffSharp />
            </Button>
            <Button
              variant="contain"
              title="Voltar para página anterior"
              onClick={() => {
                navigate(-1);
              }}
            >
              <ArrowBack />
            </Button>
          </div>
          {isVisible && (
            <Filter
              dataFilter={dataFilter}
              setDataFilter={setDataFilter}
              onClose={() => setIsVisible(false)}
            />
          )}
        </div>
        <div className={styles.containerTable}>
          <TableContainer
            columns={[
              { key: "nomeEx", titulo: "Nome", classStyle: styles.columnNome },
              { key: "tipoEx", titulo: "Tipo", classStyle: styles.columnTipo },
              { key: "statusEx", titulo: "Status", classStyle: styles.columnStatus },
              { key: "saldoInicialEx", titulo: "Saldo Inicial", classStyle: styles.columnSaldoInicial },
              { key: "actionEx", titulo: "Ações", classStyle: styles.columnAction },
            ]}
            content={
              data?.pages?.flatMap((page) =>
                page.account.map((a) => ({
                  values: {
                    nomeEx: a.nome,
                    tipoEx: a.tipo,
                    statusEx: a.ativo === 1 ? "Ativo" : "Inativo",
                    saldoInicialEx: formatCurrencyInput(a.saldo_inicial!),
                  },
                  buttons: [
                    {
                      variant: "contain",
                      typeStyle: "small",
                      title: "Editar Conta",
                      onClick: () =>
                        setIsHidden({
                          edit: true,
                          uid: a.uid_conta_financeira,
                        }),
                      icon: <Edit />,
                    },
                    {
                      variant: "contain",
                      typeStyle: ["small", "cancel"], // Regra 3: Botão de exclusão
                      title: "Deletar Conta",
                      onClick: () => handleDelete(a.uid_conta_financeira),
                      icon: <Delete />,
                    },
                  ],
                })),
              ) ?? []
            }
          />
        </div>
        <div className={styles.loadMore}>
          {hasNextPage ? (
            <Button variant="contain" onClick={() => fetchNextPage()}>
              Carregar Mais registros
            </Button>
          ) : (
            <p className={styles.tableLastItem}>
              Todos os registros foram carregados
            </p>
          )}
        </div>
      </div>
      {isHidden?.edit && (
        <Modal
          title="Editar Conta"
          uid={isHidden.uid}
          onclose={() => setIsHidden({ edit: false })}
        />
      )}
      {isHidden?.add && (
        <Modal title="Nova Conta" onclose={() => setIsHidden({ add: false })} />
      )}
      {/* Exemplo de lógica de confirmação */}
      {confirmDelete && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <p>Tem certeza que deseja excluir?</p>
            <button onClick={() => { mutate(confirmDelete); setConfirmDelete(null); }}>Sim</button>
            <button onClick={() => setConfirmDelete(null)}>Não</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialAccounts;
